import { useEffect, useState } from 'react';
import type { Exercise, SetLog } from '../../domain/types';
import { Stepper } from '../../ui/primitives';
import { IconCheck, IconTrash } from '../../ui/icons';
import { toDisplayKg, toTotalKg } from '../../domain/weight';

// Countdown timer for timed holds (plank). Starts from the target (last time's
// seconds, or 30s default), counts down, then counts up as "+overtime" so you can
// beat it. Stop records the total seconds held into the set's `reps` field.
function TimedSet({
  target, value, onCommit,
}: {
  target: number | null;
  value: number;
  onCommit: (seconds: number) => void;
}) {
  const [startTs, setStartTs] = useState<number | null>(null);
  const [now, setNow] = useState(0);
  const running = startTs != null;

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, [running]);

  const goal = target && target > 0 ? target : 30;
  const elapsed = running ? Math.max(0, (now - startTs!) / 1000) : value;
  const remaining = goal - elapsed;
  const label = running
    ? (remaining > 0 ? `${Math.ceil(remaining)}s` : `+${Math.floor(-remaining)}s`)
    : `${Math.round(value)}s`;

  function toggle() {
    if (running) {
      onCommit(Math.round((Date.now() - startTs!) / 1000));
      setStartTs(null);
    } else {
      setStartTs(Date.now());
      setNow(Date.now());
    }
  }

  return (
    <div className="row" style={{ gap: 12, alignItems: 'center' }}>
      <button type="button" className={`btn sm ${running ? 'danger' : 'primary'}`} onClick={toggle} style={{ minWidth: 72 }}>
        {running ? 'Stop' : 'Start'}
      </button>
      <div style={{ fontSize: 24, fontWeight: 800, minWidth: 64, color: running && remaining <= 0 ? 'var(--good)' : 'inherit' }}>{label}</div>
      {target != null && <div className="set-target">beat {target}s</div>}
    </div>
  );
}

// The +/- buttons jump by the exercise's own increment (leg press 5 kg, DB work
// 2.5 kg/hand, side raises 1 kg/hand, etc.) so a heavy machine doesn't need a dozen
// taps. For anything off-grid, tap the number and type the exact load. Display units
// are per-hand for dumbbells, so halve the total-kg increment for those.
function loadStep(ex: Exercise): number {
  const inc = ex.increment > 0 ? ex.increment : 2.5;
  return ex.weightEntry === 'per_side' ? inc / 2 : inc;
}

const RIR_OPTS = [
  { label: '2', value: 2 },
  { label: '1', value: 1 },
  { label: 'F', value: 0 },
];

export function SetRow({
  set, ex, index, beatReps, onChange, onToggleLogged, onRemove, canRemove,
}: {
  set: SetLog;
  ex: Exercise;
  index: number;
  beatReps: number | null;
  onChange: (patch: Partial<SetLog>) => void;
  onToggleLogged: () => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const logged = set.logged !== false;
  const weightUnit = ex.bodyweight ? '+kg' : ex.weightEntry === 'per_side' ? 'kg/hand' : 'kg';
  const weightMin = ex.bodyweight ? -60 : 0;

  if (ex.timed) {
    return (
      <div className={`card tight ${logged ? '' : 'set-pending'}`} style={{ background: logged ? 'var(--bg-elev-2)' : 'var(--bg-elev)', marginBottom: 8 }}>
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          <div className="idx" style={{ width: 22, color: 'var(--text-faint)', fontWeight: 700 }}>{index + 1}</div>
          <div className="grow"><TimedSet target={beatReps} value={set.reps} onCommit={(secs) => onChange({ reps: secs })} /></div>
          <button
            type="button"
            className={`btn sm ${logged ? 'primary' : 'ghost'}`}
            onClick={onToggleLogged}
            aria-label={logged ? 'logged' : 'mark done'}
          >
            <IconCheck width={18} height={18} /> {logged ? 'Done' : 'Log'}
          </button>
          {canRemove && (
            <button type="button" className="btn ghost sm" onClick={onRemove} aria-label="remove set">
              <IconTrash width={18} height={18} />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`card tight ${logged ? '' : 'set-pending'}`} style={{ background: logged ? 'var(--bg-elev-2)' : 'var(--bg-elev)', marginBottom: 8 }}>
      <div className="row" style={{ alignItems: 'flex-start' }}>
        <div className="idx" style={{ width: 22, paddingTop: 12, color: 'var(--text-faint)', fontWeight: 700 }}>
          {set.isWarmup ? 'W' : index + 1}
        </div>
        <div className="grow stack" style={{ gap: 8 }}>
          <div className="row wrap" style={{ gap: 10 }}>
            <Stepper
              value={toDisplayKg(set.weightKg, ex.weightEntry)}
              step={loadStep(ex)}
              min={weightMin}
              unit={weightUnit}
              onChange={(v) => onChange({ weightKg: toTotalKg(v, ex.weightEntry) })}
            />
            <div style={{ position: 'relative' }}>
              <Stepper
                value={set.reps}
                step={1}
                min={0}
                unit="reps"
                decimals={0}
                onChange={(v) => onChange({ reps: Math.round(v) })}
              />
              {beatReps != null && !set.isWarmup && (
                <div className="set-target" style={{ marginTop: 2 }}>beat {beatReps}</div>
              )}
            </div>
          </div>
          <div className="row wrap" style={{ gap: 8 }}>
            {!set.isWarmup && (
              <div className="segmented" aria-label="RIR">
                {RIR_OPTS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    className={(set.rir ?? 1) === o.value ? 'on' : ''}
                    onClick={() => onChange({ rir: o.value, isFailure: o.value === 0 })}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className={`btn sm ${set.isWarmup ? 'primary' : 'ghost'}`}
              onClick={() => onChange({ isWarmup: !set.isWarmup, rir: set.isWarmup ? 1 : null, isFailure: false })}
              title="Toggle warm-up / ramp set"
            >
              Warm-up
            </button>
            <button
              type="button"
              className={`btn sm ${logged ? 'primary' : 'ghost'}`}
              style={{ marginLeft: 'auto' }}
              onClick={onToggleLogged}
              aria-label={logged ? 'logged' : 'mark done'}
            >
              <IconCheck width={18} height={18} /> {logged ? 'Done' : 'Log'}
            </button>
            {canRemove && (
              <button type="button" className="btn ghost sm" onClick={onRemove} aria-label="remove set">
                <IconTrash width={18} height={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
